import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Observable } from "rxjs/Observable";
import { _throw as ObservableThrow } from "rxjs/observable/throw";
import { ConfigurationService } from "../configuration/configuration.service";

export interface EvaSource {
  title: string;
  space: string;
  url: string;
  score: number;
  /**
   * The Confluence page id this source was cited from (CO-3543). Optional
   * because a response from a server that predates this field carries none,
   * and the panel has to keep working with sources it cannot identify by
   * page rather than throw one away.
   */
  page_id?: string;
}

/**
 * The rating vocabulary, which is the whole vocabulary: the endpoint accepts
 * these two lowercase strings and nothing else. Declared as a type so a typo
 * cannot reach the wire and so there is no third value to mean "no rating" -
 * absence of a rating is an absent field, not a value.
 */
export type EvaRating = "up" | "down";

export interface EvaChatResponse {
  action: "ANSWER" | "REDIRECT" | "ASK_FOLLOWUP" | "CREATE_TICKET";
  /**
   * Identifier of the answer this response carries, and the thing a rating
   * points at (CO-3529). Present on every response shape - answers, refusals,
   * abstentions, deflections and a spent daily quota alike - because it is the
   * request's correlation id rather than a property of a successful answer.
   *
   * Optional here so a response without it is a typed possibility the panel has
   * to handle rather than a runtime surprise: it renders no rating control at
   * all, which is also how it behaves against a backend that predates the field.
   */
  answer_id?: string;
  answer?: string;
  message?: string;
  page_title?: string;
  page_url?: string;
  question?: string;
  reason?: string;
  sources: EvaSource[];
  usage?: { input_tokens: number; output_tokens: number; cost_usd: number };
}

export interface EvaTicketResponse {
  ticket_key: string;
  ticket_url: string;
  summary: string;
}

export interface EvaMessage {
  role: "user" | "assistant";
  content: string;
  sources?: EvaSource[];
  action?: string;
  ticketKey?: string;
  ticketUrl?: string;
  /**
   * The answer_id of the response this message was built from (CO-3529), camel
   * cased like ticketKey/ticketUrl because it is panel state rather than wire
   * shape. It rides on the MESSAGE, not on the component, because several
   * answers are on screen at once and a rating has to reach the one that was
   * rated. Absent on user messages and on anything the panel wrote itself.
   */
  answerId?: string;
  /**
   * The rating currently recorded for this message, or absent for none. Held
   * per message for the same reason answerId is, and it is what the selected
   * thumb is drawn from.
   */
  rating?: EvaRating;
  /**
   * The rating whose request is in the air for this message right now, absent
   * when none is (CO-3529). It is the in-flight guard, and it holds the VALUE
   * rather than a flag because the settling callback has to know which tap it
   * is answering for before it may write anything.
   */
  ratingInFlight?: EvaRating;
  /**
   * The last rating the server confirmed it stored for this message (CO-3529),
   * which is what a failed rating puts the thumb back to. Not the value that
   * was on screen when the tap happened: a request that a later tap has already
   * superseded captured that before the newer tap existed, so restoring it
   * un-rates an answer the server does hold a rating for.
   *
   * Both of these ride on the message, like rating and answerId and for the
   * same reason: the state is per answer, several answers are rateable at once,
   * and the message is the only thing whose lifetime is exactly the answer's -
   * clearing the chat drops it with no bookkeeping of its own.
   */
  ratingStored?: EvaRating;
}

@Injectable()
export class EvaService {
  public isOpen$ = new BehaviorSubject<boolean>(false);

  /**
   * True once the panel has been opened at least once, and it never returns to
   * false for the life of the service. CO-3559's reopen tab keys off this so it
   * only ever offers a way BACK into a panel the user has already used. The
   * ellipsis menu stays the entry point PRD REQ-001 mandates, and the tab is
   * not a second front door.
   *
   * Deliberately not persisted, so it resets on reload. A reload is not the only
   * session boundary though: signing out is an in-SPA navigation with no reload
   * anywhere in its path, so this service survives it and the flag has to be
   * cleared explicitly. See reset().
   */
  public hasBeenOpened$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService
  ) {}

  private get baseUrl(): string {
    return this.configurationService.resolveEvaUrl();
  }

  toggle(): void {
    this.setOpen(!this.isOpen$.value);
  }

  open(): void {
    this.setOpen(true);
  }

  close(): void {
    this.setOpen(false);
  }

  /**
   * Every open/close path routes through here so the hasBeenOpened$ bookkeeping
   * cannot be bypassed by a future caller reaching for isOpen$ directly.
   * hasBeenOpened$ is raised BEFORE isOpen$ emits, so a subscriber reacting to
   * the panel opening already sees the flag set.
   */
  private setOpen(open: boolean): void {
    if (open) {
      this.hasBeenOpened$.next(true);
    }
    this.isOpen$.next(open);
  }

  /**
   * Drops the panel's session state, called from StateManagementService.resetState()
   * so it runs on every sign-out path (CO-3559 AC1).
   *
   * This service is app-scoped and signing out never reloads the page, so without
   * this the next user to sign in inherits the previous one's flags: the reopen
   * tab appears before they have opened the panel, which is the criterion it fails.
   *
   * isOpen$ is cleared as well as hasBeenOpened$. A sign-out with the panel still
   * open would otherwise render it open, unasked, on the next user's first paint.
   */
  reset(): void {
    this.hasBeenOpened$.next(false);
    this.isOpen$.next(false);
  }

  chat(message: string, history: EvaMessage[]): Observable<EvaChatResponse> {
    if (!this.baseUrl) {
      return ObservableThrow(
        new Error("Eva is not configured for this environment")
      );
    }
    const wireHistory = history.map((m) => {
      const entry: { role: string; content: string; page_ids?: string[] } = {
        role: m.role,
        content: m.content,
      };
      // CO-3543: an assistant turn echoes back the page ids of the sources it
      // cited, so the server can tell "already gave this person that page"
      // apart from "nobody has ever asked me this". User turns never carry
      // this - the field describes what EVA cited, not what the user typed.
      // Absent entirely (not an empty array) when there is nothing to send,
      // which is also what an old server that predates this field expects.
      // Wire key is page_ids per the API convention ruling, matching
      // ChatMessage.PageIds's json tag in eva-chat/main.go.
      if (m.role === "assistant" && m.sources && m.sources.length) {
        const pageIds = m.sources
          .map((s) => s.page_id)
          .filter((id): id is string => !!id);
        if (pageIds.length) {
          entry.page_ids = pageIds;
        }
      }
      return entry;
    });
    return this.http.post<EvaChatResponse>(`${this.baseUrl}/api/chat`, {
      message: message,
      history: wireHistory,
    });
  }

  /**
   * Records a rating for one answer (CO-3529).
   *
   * A plain overwrite, latest wins, decided server side: re-rating the same
   * answer is the same call with the other value, so there is nothing to delete
   * and no previous value to send. Ratings do not draw on the daily question
   * allowance.
   */
  rate(answerId: string, rating: EvaRating): Observable<void> {
    if (!this.baseUrl) {
      return ObservableThrow(
        new Error("Eva is not configured for this environment")
      );
    }
    return this.http.post<void>(`${this.baseUrl}/api/ratings`, {
      answer_id: answerId,
      rating: rating,
    });
  }

  createTicket(
    summary: string,
    description: string,
    reporterName: string,
    reporterEmail: string
  ): Observable<EvaTicketResponse> {
    if (!this.baseUrl) {
      return ObservableThrow(
        new Error("Eva is not configured for this environment")
      );
    }
    return this.http.post<EvaTicketResponse>(`${this.baseUrl}/api/tickets`, {
      summary: summary,
      description: description,
      issue_type: "Task",
      priority: "Medium",
      reporter_name: reporterName,
      reporter_email: reporterEmail,
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/eva/eva.service.ts